import Link from 'next/link'
import { HTMLAttributes, forwardRef } from 'react'

import { FilterValue } from '../../../__generated__/graphql/catalog/graphql'
import { useCollectionFiltersContext } from '../../../brunswick/components/pages/Collection/sections/CollectionGridSection/containers/CollectionFiltersContext'
import { useGenerateFilterSelectionPath } from '../../../brunswick/components/pages/Collection/sections/CollectionGridSection/hooks/useGenerateFilterSelectionPath'
import { useCollectionFilterQueryParamsContext } from '../../../brunswick/containers/CollectionFilterQueryParamsContext'
import { useHeapAttributes } from '../../../containers/HeapAttributesContainer'
import { trackEvent } from '../../../lib/analytics'
import { formatPrettyColorName } from '../../../lib/utils'

import * as Styles from './styles'

type ColorFilterItemProps = {
  /* The unique key of the filter group. */
  filterKey: string
  /* The filter value object. */
  filterValue: FilterValue
  /* Render the menu item as an anchor instead of an interactive button. */
  asLink?: boolean
} & HTMLAttributes<HTMLInputElement>

/** An individal color swatch menu item to display within a list of filters. */
export const ColorFilterItem = forwardRef<HTMLInputElement, ColorFilterItemProps>(
  function ColorFilterItem({ filterKey, filterValue, asLink, onChange, ...rest }, ref) {
    const analyticsContext = useHeapAttributes()
    const { colors } = useCollectionFiltersContext()
    const generateFilterSelectionPath = useGenerateFilterSelectionPath()
    const { selectedFilters, toggleSelectedFilter } = useCollectionFilterQueryParamsContext()

    const selectedValues = selectedFilters[filterKey]
    const color = colors[filterValue.value]
    const key = `${filterKey}:${filterValue.value}`
    const path = generateFilterSelectionPath(filterKey, filterValue.value)

    const handleChange = (value: string) => {
      if (asLink) return // When filter selectors are rendered as links, the page navigation will handle updating the query params.
      toggleSelectedFilter(filterKey, value)
    }

    if (!color) return null

    const selector = (
      <Styles.OptionSwatch
        ref={ref}
        key={key}
        {...trackEvent({
          customAttributes: {
            'data-heap-filter-type': 'dropdown',
          },
          action: 'change',
          category: `${analyticsContext.category} filters`,
          label: 'color swatch',
          value: filterValue.value,
        })}
        // Optimistically display selected state while network request resolves:
        checked={filterValue.state === 'SELECTED' || !!selectedValues?.includes(filterValue.value)}
        checkmark={true}
        disabled={filterValue.state === 'DISABLED'}
        hexCodes={color.hexCodes}
        onChange={event => {
          handleChange(filterValue.value)
          onChange?.(event)
        }}
        swatchurl={color.swatchUrl}
        type='checkbox'
        value={filterValue.value}
        {...rest}
      >
        <Styles.Label>{formatPrettyColorName(filterValue.value)}</Styles.Label>
      </Styles.OptionSwatch>
    )

    return asLink ? (
      <Link key={key} href={path} shallow tabIndex={-1}>
        {selector}
      </Link>
    ) : (
      selector
    )
  }
)
